import * as React from "react";
import { DependencyList, FC, PropsWithChildren, useEffect, useRef, useState } from "react";
import { AckLogger } from "./AckLogger";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCheck, faSort, faSortDown, faSortUp, faTimes, faUpload } from "@fortawesome/free-solid-svg-icons";

library.add(faSort, faUpload, faSortDown, faSortUp, faTimes, faCheck);

/** The definition of an error message. */
export const AckErrorMessage: FC<{ errors: string[] }> = (props) => {
  const [active, setActive] = useState(true);
  const toggleState = () => setActive(!active);
  return props.errors.length > 0 && active ? (
    <article className="message is-danger is-light">
      <div className="message-header">
        <p><strong>Sorry, something went wrong :(</strong></p>
        <button className="delete" aria-label="delete" onClick={() => toggleState()}></button>
      </div>
      <div className="message-body">{props.errors}</div>
    </article>
  ) : <div/>;
};

/** A loader that displays a spinner while loading. */
export const AckLoader: FC<{ isLoading: boolean }> = (props) => {
  return props.isLoading ? (
    <div className="box is-centered">
      <div className="loader is-loading p-5"/>
    </div>
  ) : <div/>;
};

/** A hook that loads a block of code and displays a loader while it is loading. */
export const useEffectLoader = (loader: SyncLoader, load: (context?: any) => void, deps?: DependencyList) => {
  const dependencies = deps == null ? [] : deps;
  useEffect(() => {
    const context = { isMounted: true };
    loader.load(async () => load(context), context);
    return () => {
      context.isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
};

/** A type describing a synchronous loader. */
export type SyncLoader = {
  load: (block: () => Promise<any>, context?: any) => void;
  errors: string[];
  isLoading: boolean;
};

/** A type describing an asynchronous loader. */
export type AsyncLoader = {
  load: (block: () => any, context?: any) => void;
  errors: string[];
  isLoading: boolean;
};

/** A hook that creates a synchronous loader. */
export const useLoader: () => SyncLoader = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState([] as string[]);
  const load = (block: () => Promise<any>, context?: any) => {
    AckLogger.info("Start loading block");
    const loaderContext = context ? context : { isMounted: true };
    if (loaderContext.isMounted) setIsLoading(true);
    block()
      .then(() => {
        if (loaderContext.isMounted) setErrors([]);
      })
      .catch(err => {
        AckLogger.error("Error while sync loading page", err);
        if (loaderContext.isMounted) setErrors(err.message ? [err.message] : ["Error while loading page"]);
      })
      .finally(() => {
        AckLogger.info("Finished sync loading block");
        if (loaderContext.isMounted) setIsLoading(false);
      });
  };
  return { load: load, errors: errors, isLoading: isLoading };
};

/** A hook that creates an asynchronous loader. */
export const useAsyncLoader: () => AsyncLoader = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState([] as string[]);
  const load = async (block: () => void, context?: any) => {
    const loaderContext = context ? context : { isMounted: true };
    try {
      if (loaderContext.isMounted) setIsLoading(true);
      block();
      if (loaderContext.isMounted) setErrors([]);
    } catch (err: unknown) {
      AckLogger.error("Error while async loading page", err);
      if (loaderContext.isMounted) setErrors(typeof err === "string" ? [err] : ["Error while loading page"]);
    } finally {
      if (loaderContext.isMounted) setIsLoading(false);
      AckLogger.info("Finished async loading block");
    }
  };
  return { load: load, errors: errors, isLoading: isLoading };
};

/** Formats a number as a currency. */
export const formatCurrency = (value: number, locale: string = "en-US"): string => {
  // FIXME: pass locale and currency as parameter
  return new Intl.NumberFormat(locale, { style: "currency", currency: "USD", maximumFractionDigits: 4 }).format(value);
};

/** Formats a number as a decimal. */
export const formatDecimal = (value: number, locale: string = "en-US"): string => {
  return new Intl.NumberFormat(locale).format(value);
};

/** Formats a number as a percentage. */
export const formatPercentage = (value: number, locale: string = "en-US"): string => {
  return formatDecimal(value * 100, locale) + "%";
};

/** Formats a number as a date. */
export const formatDate = (value: number): string => {
  return new Date(value * 1000).toISOString().substring(0, 10);
};

/** Formats a number as a date and time. */
export const formatDateTime = (value: number): string => {
  return new Date(value * 1000).toISOString().substring(0, 16);
};

/** A simple navbar dropdown component. */
export const NavbarDropdown: FC<PropsWithChildren<{ title: string }>> = (props) => {
  const [isActive, setActive] = useState(false);
  const dropdown = useRef(null as HTMLDivElement | null);
  const toggle = () => {
    setActive(!isActive);
    const links = dropdown.current?.querySelectorAll(".navbar-item") || [];
    links.forEach((link: Element) => link.addEventListener("click", () => setActive(false)));
  };
  const handleClickOutside = (event: MouseEvent) => {
    if (!dropdown?.current?.contains(event.target as Node) && dropdown?.current?.getAttribute("data-active") === "true") {
      setActive(false);
    }
  };
  useEffect(() => {
    window.addEventListener("mousemove", handleClickOutside);
    // returned function will be called on component unmount
    return () => window.removeEventListener("mousemove", handleClickOutside);
  }, []);
  return (
    <div className={"navbar-item has-dropdown" + (isActive ? " is-active" : "")} ref={dropdown} data-active={isActive}>
      <a className="navbar-link" onClick={toggle}>{props.title}</a>
      <div className="navbar-dropdown">
        {props.children}
      </div>
    </div>
  );
};

